/**
 * The long-move runner (ท่ายาว, design docs/design/combat/2026-10-11-long-moves.md §1.1, §4.2): ท่าใหญ่ as 3–5 s set pieces,
 * and the client's beat events for the lock routines too.
 *
 * ท่าใหญ่ (a move with a `kind: 'ult'` entry in data/longMoves):
 * - Gate: a FULL rage bar (`RAGE.max`), all of it spent on the press. The move becomes scripted with armour (the World runs
 *   only its wind-up, the tell, and the caster is neither stunned nor interrupted out of it; red in a bout, data/duelArmor.ts).
 * - Tell (`opener.tell` s): `longTell` carries the opener's real area (field ring round the caster, ground circle at the spot
 *   fixed at the press, lunge lane, volley line) for the renderer, from the first frame. The caster is held still.
 * - Opener at the tell's end: every foe inside (nearest first, at most `LONG_ULT.pveCap`), not in dodge i-frames, not held by
 *   anyone else, not inside its held immunity. Bosses (`isLongBoss`) are not held: they take each beat in reach and keep acting.
 *   Nobody caught: the whiff — `whiff.rageBack` rage back, the caster held for `whiff.stop` s (opened in a bout).
 * - Piece (`len` s from the catch): every caught foe held (commands dropped, AI stunned), the beats' blows land on time
 *   (`power × share`, spread over each beat's first 60%), `knock` beats throw the foe back (≤ `LONG_ULT.maxKnock`, never past
 *   ring radius − `ringMargin`), `pass`/`blink` beats carry the caster through the foe. At the `fall` beat a bout foe is handed
 *   to the knock-down rules (get-up choices); elsewhere it is let go at the `recover` beat. `LONG_ULT.heldImmunity` s after a
 *   release no new hold starts on it (ult→ult here; lock→ult watched below). No escape (main, v1).
 * - Guard (main): a guarding foe IS caught; while its guard holds each blow lands × `LONG_ULT.guardMult` and drains
 *   `LONG_ULT.guardDrain` guard points; the blow that finds it short breaks the guard (a `break` through the triangle: the crack),
 *   the rest land in full. Outside a bout (no guard points) the guard just cuts every blow.
 *
 * Events (data/longMoves/types.ts `LongMoveEvent`): `longTell`, `longStart`, `longBeat` (every beat's start, index into the
 * def's `beats`), `longEnd`. The lock routines (systems/duelLockOpen.ts runs them) get `longStart` / `longBeat` / `longEnd`
 * from here as well, watched from the outside: one event stream for the client's poses, FX and sound.
 * Deterministic: time from `dt`, no draws of its own (the blows go through the World's `hit`).
 */
import { DUEL_FLAGS } from '../data/duelMatch';
import { lockRoutineOf } from '../data/lockRoutines';
import { LONG_ULT, isLongBoss, longMoveOf, longMovesOf } from '../data/longMoves';
import type { LongBeat, LongMoveDef, LongMoveEvent } from '../data/longMoves/types';
import { RAGE, SKILLS } from '../data/skills';
import { dist, type Entity, type PlayerCommand, type WorldEvent } from '../world';
import { addShoveHold } from './duelHolds';
import { duelKnockOf } from './duelKnock';
import { duelLockOf } from './duelLock';
import { duelLockOpenOf } from './duelLockOpen';
import { duelMatchOf } from './duelMatch';
import { duelRecoveryOf } from './duelRecovery';
import { skillBarOf } from './skillBar';
import type { SystemHost, WorldSystem } from './types';

/** The caster's distance band from the held foe during a lock routine (metres). */
const SPACING = [1.3, 1.6] as const;

const SYSTEMS = new WeakMap<object, LongMoveSystem>();
export const longMoveSysOf = (w: object): LongMoveSystem | null => SYSTEMS.get(w) ?? null;

/** What a held (or casting) player cannot do. */
const FROZEN = new Set<PlayerCommand['t']>([
  'moveDir',
  'moveTo',
  'skill',
  'block',
  'dash',
  'jump',
  'attack',
  'engage',
  'sprint',
  'mount',
  'meditate',
  'sheathe',
  'interact',
  'fly',
  'slide',
]);

interface Tell {
  skillId: string;
  def: LongMoveDef;
  x: number;
  y: number;
  dirX: number;
  dirY: number;
  tgt: number | null;
  bout: boolean;
}

interface Held {
  id: number;
  /** `up`: guarding, guard holding; `broken`: broke during the piece; `none`: not guarding. */
  guard: 'up' | 'broken' | 'none';
  free: boolean;
  knock: number;
}

interface Piece {
  src: number;
  skillId: string;
  def: LongMoveDef;
  t: number;
  cx: number;
  cy: number;
  held: Held[];
  struck: number[];
  /** Blows landed so far, the beat last announced. */
  done: number;
  beat: number;
  bout: boolean;
  pass: { vx: number; vy: number; left: number } | null;
}

/** A piece's state for the renderer and tests. */
export interface LongView {
  src: number;
  skillId: string;
  t: number;
  len: number;
  beat: number;
  held: number[];
  struck: number[];
}

export class LongMoveSystem implements WorldSystem {
  readonly name = 'longMove';
  private readonly tells = new Map<number, Tell>();
  private fired: number[] = [];
  private readonly pieces = new Map<number, Piece>();
  /** Caster -> seconds left of its whiff hold. */
  private readonly spent = new Map<number, number>();
  /** Fighter -> world time until which no new hold may start on it. */
  private readonly immune = new Map<number, number>();
  /** Lock routines watched (caster -> its foe, move and the beat last announced). */
  private readonly locks = new Map<number, { tgt: number; skillId: string; beat: number }>();
  /** Lock routines running (caster -> foe, move, blows landed): the engine for locks; duelLockOpen starts and cuts them. */
  private readonly lockRuns = new Map<
    number,
    {
      tgt: number;
      skillId: string;
      done: number;
      sealed?: boolean;
      /** `pass` beats started (index), and the caster's travel through the foe still to go. */
      passed?: number;
      pass?: { vx: number; vy: number; left: number; fx: number; fy: number };
    }
  >();
  /** Fighters in a lock form last step (for lock→ult immunity). */
  private inLock = new Set<number>();
  /** Counts for the tests and sims. */
  readonly tally = { tells: 0, caught: 0, whiffs: 0, blows: 0, guardBreaks: 0, cut: 0 };

  constructor(private readonly h: SystemHost) {
    SYSTEMS.set(h.world, this);
  }

  setup(): void {
    // Each ult becomes scripted with armour, its wind-up the tell (idempotent: the same values every World).
    for (const d of longMovesOf('ult')) {
      const s = SKILLS[d.skillId] as { scripted?: boolean; armour?: boolean; windup: number } | undefined;
      if (!s) continue;
      s.scripted = true;
      s.armour = true;
      s.windup = d.opener.tell;
    }
    addShoveHold(this.h.world, (id) => this.pieces.has(id) || this.heldIn(id) !== null);
  }

  /** The piece an entity is held in (as a foe), or null. */
  private heldIn(id: number): Piece | null {
    for (const p of this.pieces.values()) if (p.held.some((x) => x.id === id && !x.free)) return p;
    return null;
  }

  /** Whether an entity is held by a long move: telling, in a piece (caster or held foe) or in a whiff's stop. */
  isHeld(id: number): boolean {
    return this.tells.has(id) || this.pieces.has(id) || this.spent.has(id) || this.heldIn(id) !== null;
  }

  /** The piece a caster runs or a foe is held in (null: none). */
  view(id: number): LongView | null {
    const p = this.pieces.get(id) ?? this.heldIn(id);
    if (!p) return null;
    return {
      src: p.src,
      skillId: p.skillId,
      t: p.t,
      len: p.def.len,
      beat: p.beat,
      held: p.held.filter((x) => !x.free).map((x) => x.id),
      struck: [...p.struck],
    };
  }

  /** Seconds of held immunity left on a fighter. */
  immuneLeft(id: number): number {
    return Math.max(0, (this.immune.get(id) ?? 0) - this.h.world.time);
  }

  private emitL(e: LongMoveEvent): void {
    this.h.emit(e);
  }

  private inBout(id: number): boolean {
    const b = this.h.world.pvpBout;
    return DUEL_FLAGS.v2 && !!b && b.fight && (b.a === id || b.b === id);
  }

  refuse(cmd: PlayerCommand): boolean {
    const w = this.h.world;
    const p = w.player;
    if (this.isHeld(p.id) && FROZEN.has(cmd.t)) return true;
    if (cmd.t === 'skill') {
      const id = skillBarOf(w.progress)[cmd.slot];
      if (id && longMoveOf(id)?.kind === 'ult' && p.rage < RAGE.max - 1e-6 && !p.dead) {
        this.h.notice(`ความโกรธไม่พอ (ต้องการ ${RAGE.max})`);
        return true;
      }
    }
    return false;
  }

  observe(e: WorldEvent): void {
    if (e.type === 'castStart') this.tell(e.srcId, e.skillId);
    else if (e.type === 'skill' && this.tells.get(e.srcId)?.skillId === e.skillId) this.fired.push(e.srcId);
  }

  /** The tell: the full bar spent (or the cast refused), the area announced, the caster held still. */
  private tell(srcId: number, skillId: string): void {
    const def = longMoveOf(skillId);
    if (def?.kind !== 'ult') return;
    const w = this.h.world;
    const c = w.get(srcId);
    const s = SKILLS[skillId];
    if (!c || !c.cast || !s) return;
    // A backstop for presses that skip `refuse` (auto-battle): the bar must have been full before the World took its share.
    if (c.rage + (s.rage ?? 0) < RAGE.max - 1e-6) {
      c.cast = null;
      c.rage += s.rage ?? 0;
      delete c.cooldowns[skillId];
      return;
    }
    c.rage = 0;
    const o = def.opener;
    const tgt = w.get(c.cast.targetId ?? -1) ?? this.nearestFoe(c, Math.max(o.range, o.radius ?? 0) + 0.4);
    let x = c.x;
    let y = c.y;
    if (o.type === 'ground') ({ x, y } = c.cast.at ?? tgt ?? c);
    let dirX = c.dirX;
    let dirY = c.dirY;
    if (tgt && (o.type === 'lunge' || o.type === 'volley')) {
      const l = dist(c, tgt) || 1;
      dirX = (tgt.x - c.x) / l;
      dirY = (tgt.y - c.y) / l;
    }
    const t: Tell = { skillId, def, x, y, dirX, dirY, tgt: tgt?.id ?? null, bout: this.inBout(c.id) };
    this.tells.set(c.id, t);
    this.tally.tells++;
    this.still(c.id, false);
    this.emitL({
      type: 'longTell',
      src: c.id,
      skillId,
      shape: o.type,
      x,
      y,
      radius: o.radius ?? 0,
      range: o.range,
      dirX,
      dirY,
      tell: c.cast.windup,
      tgtId: t.tgt,
    });
  }

  private hostileTo(c: Entity, o: Entity): boolean {
    if (o === c || o.dead || o.kind === 'pet' || o.kind === 'ambient') return false;
    const b = this.h.world.pvpBout;
    const pair = !!b && (b.a === c.id || b.b === c.id) && (b.a === o.id || b.b === o.id);
    return pair || this.h.world.isHostile(c, o);
  }

  private nearestFoe(c: Entity, reach: number): Entity | undefined {
    let best: Entity | undefined;
    let bd = Infinity;
    for (const o of this.h.world.entities.values()) {
      if (!this.hostileTo(c, o)) continue;
      const d = dist(c, o);
      if (d <= reach && (d < bd || (d === bd && best && o.id < best.id))) {
        best = o;
        bd = d;
      }
    }
    return best;
  }

  /** Who the opener catches (held) and strikes (bosses), nearest first, capped. */
  private opener(c: Entity, t: Tell): { held: Entity[]; struck: Entity[] } {
    const w = this.h.world;
    const o = t.def.opener;
    const lock = duelLockOf(w);
    const now = w.time;
    const free = (e: Entity) =>
      e.invulnT <= 0 && !lock?.inForm(e.id) && !this.isHeld(e.id) && (this.immune.get(e.id) ?? -1) <= now;
    let at: { x: number; y: number } = { x: t.x, y: t.y };
    let pool: Entity[] = [];
    if (o.type === 'field' || o.type === 'ground') {
      for (const e of w.entities.values())
        if (this.hostileTo(c, e) && Math.hypot(e.x - t.x, e.y - t.y) <= (o.radius ?? 0)) pool.push(e);
    } else {
      const tg = w.get(t.tgt ?? -1) ?? this.nearestFoe(c, o.range + 0.4);
      if (tg && this.hostileTo(c, tg) && dist(c, tg) <= o.range + 0.4) pool = [tg];
      at = c;
    }
    pool = pool.filter((e) => isLongBoss(e) || free(e));
    pool.sort(
      (a, b) => Math.hypot(a.x - at.x, a.y - at.y) - Math.hypot(b.x - at.x, b.y - at.y) || a.id - b.id,
    );
    pool = pool.slice(0, LONG_ULT.pveCap);
    return { held: pool.filter((e) => !isLongBoss(e)), struck: pool.filter((e) => isLongBoss(e)) };
  }

  /** Still a player's controls (and an AI's moves) for a hold; `guard` keeps a guard raised. */
  private still(id: number, guard: boolean): void {
    const w = this.h.world;
    const inp = w.players.get(id)?.input;
    if (inp) Object.assign(inp, { dx: 0, dy: 0, block: guard, engage: false, sprint: false });
    const e = w.get(id);
    if (e) Object.assign(e, { path: null, dashT: 0, blocking: guard });
  }

  private resolve(cid: number): void {
    const w = this.h.world;
    const t = this.tells.get(cid);
    this.tells.delete(cid);
    const c = w.get(cid);
    if (!t || !c || c.dead) return;
    const { held, struck } = this.opener(c, t);
    if (!held.length && !struck.length) {
      // The whiff: a third of the bar back, the caster open for `stop` seconds.
      const wf = t.def.whiff!;
      c.rage = Math.min(c.maxRage || RAGE.max, c.rage + wf.rageBack);
      this.spent.set(cid, wf.stop);
      if (this.inBout(cid)) duelRecoveryOf(w)?.startStop(cid, wf.stop, 'ult-whiff');
      this.tally.whiffs++;
      this.emitL({ type: 'longEnd', src: cid, skillId: t.skillId, kind: 'ult', outcome: 'whiff' });
      return;
    }
    const match = duelMatchOf(w);
    const p: Piece = {
      src: cid,
      skillId: t.skillId,
      def: t.def,
      t: 0,
      cx: t.def.opener.type === 'field' || t.def.opener.type === 'ground' ? t.x : c.x,
      cy: t.def.opener.type === 'field' || t.def.opener.type === 'ground' ? t.y : c.y,
      held: held.map((e) => ({
        id: e.id,
        guard: e.blocking && !(this.inBout(e.id) && (match?.brokenLeft(e.id) ?? 0) > 0) ? 'up' : 'none',
        free: false,
        knock: 0,
      })),
      struck: struck.map((e) => e.id),
      done: 0,
      beat: 0,
      bout: t.bout,
      pass: null,
    };
    // A lunge carries the caster in; every held foe is drawn a little toward the centre.
    if (t.def.opener.type === 'lunge' && held[0]) {
      const d = dist(c, held[0]);
      if (d > LONG_ULT.close)
        this.h.move(
          c,
          ((held[0].x - c.x) / d) * (d - LONG_ULT.close),
          ((held[0].y - c.y) / d) * (d - LONG_ULT.close),
        );
      p.cx = c.x;
      p.cy = c.y;
    }
    for (const e of held) {
      const d = Math.hypot(p.cx - e.x, p.cy - e.y);
      const k = Math.min(LONG_ULT.pull, Math.max(0, d - LONG_ULT.close));
      if (k > 0) this.h.move(e, ((p.cx - e.x) / d) * k, ((p.cy - e.y) / d) * k);
      e.cast = null;
      e.spinT = 0;
    }
    this.pieces.set(cid, p);
    this.tally.caught += held.length + struck.length;
    this.holdAll(p);
    this.emitL({
      type: 'longStart',
      src: cid,
      skillId: p.skillId,
      kind: 'ult',
      held: held.map((e) => e.id),
      struck: [...p.struck],
    });
    this.announce(p, c);
  }

  /** Beat `p.beat` starts: the event, and the caster's displacement for it. */
  private announce(p: Piece, c: Entity): void {
    const b = p.def.beats[p.beat]!;
    const live = p.held.filter((x) => !x.free).map((x) => x.id);
    this.emitL({
      type: 'longBeat',
      src: p.src,
      skillId: p.skillId,
      kind: 'ult',
      i: p.beat,
      held: live,
      struck: [...p.struck],
    });
    const mv = b.move;
    if (!mv || (mv.kind !== 'pass' && mv.kind !== 'blink')) return;
    const foe = this.h.world.get(live[0] ?? -1);
    const fx = foe ? foe.x : p.cx;
    const fy = foe ? foe.y : p.cy;
    let ux = fx - c.x;
    let uy = fy - c.y;
    const l = Math.hypot(ux, uy);
    if (l < 1e-6) {
      ux = c.dirX;
      uy = c.dirY;
    } else {
      ux /= l;
      uy /= l;
    }
    // Through the foe: a pass travels its distance (or twice the gap), a blink lands as far behind the foe as it stood before.
    const d = mv.dist ?? Math.max(2 * l, 2.4);
    if (mv.kind === 'blink') this.h.move(c, ux * d, uy * d);
    else
      p.pass = {
        vx: (ux * d) / LONG_ULT.passTime,
        vy: (uy * d) / LONG_ULT.passTime,
        left: LONG_ULT.passTime,
      };
  }

  /** One blow of a beat on every held foe (guard rule) and every boss in reach; the caster's rage left as it was, the foe's as `rageToFoe`. */
  private blow(p: Piece, c: Entity, b: LongBeat, pw: number): void {
    const w = this.h.world;
    const match = duelMatchOf(w);
    this.tally.blows++;
    for (const x of p.held) {
      const e = w.get(x.id);
      if (x.free || !e || e.dead) continue;
      const rage = [c.rage, e.rage];
      this.strike(p, c, e, x, match, pw);
      if (!LONG_ULT.rageToCaster) c.rage = rage[0]!;
      if (!LONG_ULT.rageToFoe) e.rage = rage[1]!;
    }
    const reach = b.radius ?? p.def.opener.radius ?? 3;
    for (const id of p.struck) {
      const e = w.get(id);
      if (e && !e.dead && dist(c, e) <= reach + 0.5) this.h.hit(c, e, pw, 'power', p.skillId, 0);
    }
  }

  private strike(
    p: Piece,
    c: Entity,
    e: Entity,
    x: Held,
    match: ReturnType<typeof duelMatchOf>,
    pw: number,
  ): void {
    if (x.guard === 'up') {
      if (p.bout && match) {
        if (match.spendGuard(e.id, LONG_ULT.guardDrain))
          this.h.hit(c, e, pw * LONG_ULT.guardMult, 'power', p.skillId, 0);
        else {
          // Short of guard points: this blow breaks the guard (the triangle's break: the crack), the rest land in full.
          e.blocking = true;
          const out = this.h.hit(c, e, pw, 'break', p.skillId, 0);
          x.guard = 'broken';
          if (out === 'guardBreak') this.tally.guardBreaks++;
        }
      } else this.h.hit(c, e, pw * LONG_ULT.guardMult, 'power', p.skillId, 0);
      if (x.guard === 'up') e.blocking = true;
    } else this.h.hit(c, e, pw, 'power', p.skillId, 0);
  }

  /** Lets a held foe go (stun cut, guard dropped) and starts its held immunity. */
  private release(x: Held): void {
    const w = this.h.world;
    x.free = true;
    const e = w.get(x.id);
    this.immune.set(x.id, w.time + LONG_ULT.heldImmunity);
    if (!e) return;
    e.stunT = Math.min(e.stunT, 0.05);
    e.blocking = false;
    const inp = w.players.get(x.id)?.input;
    if (inp) inp.block = false;
  }

  private end(p: Piece, outcome: 'done' | 'cut'): void {
    this.pieces.delete(p.src);
    for (const x of p.held) if (!x.free) this.release(x);
    if (outcome === 'cut') this.tally.cut++;
    this.emitL({ type: 'longEnd', src: p.src, skillId: p.skillId, kind: 'ult', outcome });
  }

  /** Holds the caster and every held foe for this step. */
  private holdAll(p: Piece): void {
    const w = this.h.world;
    const c = w.get(p.src);
    if (!c) return;
    this.still(c.id, false);
    c.stunT = 0; // red through the piece: nothing stuns it out
    c.cast = null;
    c.attackT = Math.max(c.attackT, 0.1);
    let first: Entity | undefined;
    for (const x of p.held) {
      const e = w.get(x.id);
      if (x.free || !e || e.dead) continue;
      first ??= e;
      e.stunT = Math.max(e.stunT, 0.1);
      e.cast = null;
      this.still(e.id, x.guard === 'up');
      this.h.face(e, c);
    }
    if (first && !p.pass) this.h.face(c, first);
  }

  private tick(p: Piece, dt: number): void {
    const w = this.h.world;
    const c = w.get(p.src);
    if (!c || c.dead || (p.bout && !this.inBout(p.src))) return this.end(p, 'cut');
    p.t += dt;
    const beats = p.def.beats;
    while (p.beat + 1 < beats.length && p.t >= beats[p.beat + 1]!.at) {
      p.beat++;
      this.announce(p, c);
    }
    // The blows, on time.
    let n = 0;
    outer: for (let i = 0; i < beats.length; i++) {
      const b = beats[i]!;
      const endAt = beats[i + 1]?.at ?? p.def.len;
      for (let j = 0; j < b.hits; j++, n++) {
        if (n < p.done) continue;
        const at = b.at + ((j + 0.5) / b.hits) * LONG_ULT.blowSpread * (endAt - b.at);
        if (p.t < at) break outer;
        p.done++;
        c.swingT = Math.max(c.swingT, Math.min(0.3, (endAt - b.at) / b.hits));
        this.blow(p, c, b, (p.def.power * b.share) / b.hits);
      }
    }
    // The caster's pass through the foe.
    if (p.pass) {
      const s = Math.min(dt, p.pass.left);
      this.h.move(c, p.pass.vx * s, p.pass.vy * s);
      p.pass.left -= s;
      if (p.pass.left <= 1e-9) p.pass = null;
    }
    const b = beats[p.beat]!;
    const ring = p.bout ? duelMatchOf(w)?.current?.ring : undefined;
    for (const x of p.held) {
      const e = w.get(x.id);
      if (x.free || !e) continue;
      if (e.dead) {
        x.free = true;
        continue;
      }
      // A knock beat throws the foe back, within the piece's budget and short of the ring's edge.
      if (b.foe === 'knock' && b.dist && x.knock < LONG_ULT.maxKnock) {
        const len = (beats[p.beat + 1]?.at ?? p.def.len) - b.at;
        const step = Math.min((b.dist / Math.max(1e-6, len)) * dt, LONG_ULT.maxKnock - x.knock);
        let ux = e.x - c.x;
        let uy = e.y - c.y;
        const l = Math.hypot(ux, uy) || 1;
        ux /= l;
        uy /= l;
        let ok = true;
        if (ring) {
          const d0 = Math.hypot(e.x - ring.x, e.y - ring.y);
          const d1 = Math.hypot(e.x + ux * step - ring.x, e.y + uy * step - ring.y);
          ok = d1 <= Math.max(d0, ring.r - LONG_ULT.ringMargin);
        }
        if (ok && this.h.move(e, ux * step, uy * step)) x.knock += step;
      }
      // Down at the fall beat: a bout foe goes to the knock-down rules (get-up choices); elsewhere it is let go at `recover`.
      if (b.foe === 'fall' && p.bout) {
        this.release(x);
        duelKnockOf(w)?.downFrom(x.id, c.id);
      } else if (b.foe === 'recover') this.release(x);
    }
    // The caster plays the piece out to its end (the release beats too), whoever is still held.
    if (p.t >= p.def.len) return this.end(p, 'done');
    this.holdAll(p);
  }

  /** A lock routine starts (duelLockOpen's read said `full`). */
  startLock(cid: number, tgt: number, skillId: string): void {
    this.lockRuns.set(cid, { tgt, skillId, done: 0 });
  }
  cancelLock(cid: number): void {
    this.lockRuns.delete(cid);
  }
  clearLocks(): void {
    this.lockRuns.clear();
  }

  /** Runs the lock routines one step (called by duelLockOpen.step, in the fight, so the order of the step is as it was). */
  stepLocks(dt: number): void {
    const w = this.h.world;
    const lock = duelLockOf(w);
    for (const [cid, run] of [...this.lockRuns]) {
      const info = lock?.formInfo(cid);
      const c = w.get(cid);
      const t = w.get(run.tgt);
      const rt = lockRoutineOf(run.skillId);
      if (!info || !c || !t || !rt || info.tgt !== run.tgt) {
        this.lockRuns.delete(cid);
        continue;
      }
      // At its fall beat the routine hands the foe to the knock-down rules (systems/duelKnock.ts): down, with the get-up
      // choices (rise, guard, roll, rising attack, stay down) in place of a scripted recovery.
      const fall = rt.r.beats.find((b) => b.foe === 'fall');
      const knock = duelKnockOf(w);
      // The routine's `endStatus` (chain-hold's shackle) lands once, as the foe is let go: at its fall or recover beat (the
      // last beat without either). Not on an escape or a cut (the run is gone before then). Put on without a source: the
      // routine's own payload, not counted in the PvP diminishing returns its blows' statuses already used up (statusRules).
      const es = rt.r.endStatus;
      const letGo = (fall ?? rt.r.beats.find((b) => b.foe === 'recover') ?? rt.r.beats[rt.r.beats.length - 1]!).at;
      if (es && !run.sealed && info.t >= letGo) {
        run.sealed = true;
        w.addStatus(t, es.status, es.stacks ?? 1);
        const st = t.statuses.find((x) => x.id === es.status);
        if (st) st.t = es.dur;
      }
      if (fall && knock && info.t >= fall.at) {
        lock?.cut(cid);
        this.lockRuns.delete(cid);
        knock.downFrom(run.tgt, cid);
        continue;
      }
      // The routine's blows, on time.
      let n = 0;
      rt.r.beats.forEach((b, i) => {
        const end = rt.r.beats[i + 1]?.at ?? rt.r.len;
        for (let j = 0; j < b.hits; j++, n++) {
          if (n < run.done) continue;
          const at = b.at + ((j + 0.5) / b.hits) * 0.6 * (end - b.at);
          if (info.t < at) return;
          run.done++;
          c.swingT = Math.max(c.swingT, Math.min(0.3, (end - b.at) / b.hits));
          this.h.hit(c, t, (rt.r.power * b.share) / b.hits, 'attack', run.skillId, 0);
        }
      });
      // A `pass` beat (one-cut, quick-draw-sabre: the cut that ends behind the foe): the caster travels through the held foe
      // over `LONG_ULT.passTime` to `move.dist` metres along the line (default: as far beyond as it stood before), then the
      // form is re-aimed so later knock beats throw the foe away from where the caster now stands. No other routine has one.
      rt.r.beats.forEach((b, i) => {
        if (b.move?.kind !== 'pass' || i <= (run.passed ?? -1) || info.t < b.at) return;
        run.passed = i;
        let ux = t.x - c.x;
        let uy = t.y - c.y;
        const l = Math.hypot(ux, uy);
        if (l < 1e-6) {
          ux = c.dirX;
          uy = c.dirY;
        } else {
          ux /= l;
          uy /= l;
        }
        const d = b.move.dist ?? Math.max(2 * l, 2.4);
        run.pass = { vx: (ux * d) / LONG_ULT.passTime, vy: (uy * d) / LONG_ULT.passTime, left: LONG_ULT.passTime, fx: t.x, fy: t.y };
      });
      if (run.pass) {
        const s = Math.min(dt, run.pass.left);
        this.h.move(c, run.pass.vx * s, run.pass.vy * s);
        // the held foe stands its ground while the caster goes through (the bodies' push-apart would carry it along)
        Object.assign(t, { x: run.pass.fx, y: run.pass.fy });
        run.pass.left -= s;
        if (run.pass.left <= 1e-9) {
          run.pass = undefined;
          this.h.face(c, t);
          lock?.turnAxis(cid);
        }
        continue;
      }
      // Until the big blow the caster keeps a sword's length from the held foe (blows shove it; the lunge may close in too
      // far): it presses in, or gives a little ground, never overlapping the figures.
      const big = rt.r.beats.reduce((m, b) => (b.share > m.share ? b : m)).at;
      const dx = t.x - c.x;
      const dy = t.y - c.y;
      const d = Math.hypot(dx, dy) || 1e-6;
      const [lo, hi] = rt.r.spacing ?? SPACING; // a ranged routine keeps its own band (pulse-seal)
      const off = d > hi ? d - lo : d < lo - 0.05 ? d - lo : 0;
      if (info.t > rt.r.catch && info.t < big && off !== 0) {
        const step = Math.sign(off) * Math.min(Math.abs(off), 3 * dt);
        this.h.move(c, (dx / d) * step, (dy / d) * step);
        this.h.face(c, t);
      }
    }
  }
  /** The lock routines (systems/duelLockOpen.ts starts them): their beat events, and lock→ult held immunity. */
  private watchLocks(): void {
    const w = this.h.world;
    const b = w.pvpBout;
    const open = duelLockOpenOf(w);
    const lock = duelLockOf(w);
    const ids = DUEL_FLAGS.v2 && b && b.fight ? [b.a, b.b] : [];
    const nowIn = new Set<number>();
    for (const id of ids) {
      if (lock?.inForm(id)) nowIn.add(id);
      const v = open?.formView(id);
      if (!v || v.caster !== id || longMoveOf(v.skillId)?.kind !== 'lock') continue;
      let L = this.locks.get(id);
      if (!L || L.tgt !== v.target || L.skillId !== v.skillId) {
        if (L) this.emitL({ type: 'longEnd', src: id, skillId: L.skillId, kind: 'lock', outcome: 'done' });
        L = { tgt: v.target, skillId: v.skillId, beat: -1 };
        this.locks.set(id, L);
        this.emitL({
          type: 'longStart',
          src: id,
          skillId: v.skillId,
          kind: 'lock',
          held: [v.target],
          struck: [],
        });
      }
      while (L.beat < v.beat) {
        L.beat++;
        this.emitL({
          type: 'longBeat',
          src: id,
          skillId: v.skillId,
          kind: 'lock',
          i: L.beat,
          held: [v.target],
          struck: [],
        });
      }
    }
    for (const [id, L] of [...this.locks]) {
      const v = open?.formView(id);
      if (v && v.caster === id && v.skillId === L.skillId && ids.includes(id)) continue;
      this.locks.delete(id);
      const esc = open?.lastEscape;
      const escaped = !!esc && esc.caster === id && esc.id === L.tgt && w.time - esc.at < 0.1;
      this.emitL({
        type: 'longEnd',
        src: id,
        skillId: L.skillId,
        kind: 'lock',
        outcome: escaped ? 'escaped' : 'done',
      });
    }
    for (const id of this.inLock) if (!nowIn.has(id)) this.immune.set(id, w.time + LONG_ULT.heldImmunity);
    this.inLock = nowIn;
  }

  step(dt: number): void {
    const w = this.h.world;
    this.watchLocks();
    // The opener, once the tell is over (after the bout's recovery has had its say this step).
    const fired = this.fired;
    this.fired = [];
    for (const id of fired) this.resolve(id);
    for (const [id, t] of [...this.tells]) {
      const c = w.get(id);
      if (!c || c.dead || !c.cast || c.cast.skillId !== t.skillId || (t.bout && !this.inBout(id))) {
        this.tells.delete(id);
        this.tally.cut++;
        this.emitL({ type: 'longEnd', src: id, skillId: t.skillId, kind: 'ult', outcome: 'cut' });
      } else this.still(id, false);
    }
    for (const p of [...this.pieces.values()]) this.tick(p, dt);
    for (const [id, left] of [...this.spent]) {
      const c = w.get(id);
      if (!c || c.dead || left - dt <= 0) {
        this.spent.delete(id);
        continue;
      }
      this.spent.set(id, left - dt);
      this.still(id, false);
      c.attackT = Math.max(c.attackT, 0.05);
    }
    for (const [id, until] of [...this.immune]) if (until <= w.time) this.immune.delete(id);
  }
}
